import React, { useState, useEffect } from 'react';
import { useLanguage } from '@/i18n/LanguageContext';
import {
  AcademicCapIcon,
  DocumentTextIcon,
  ArrowDownTrayIcon,
  EyeIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  MagnifyingGlassIcon
} from '@heroicons/react/24/outline';
import { MOCK_DATA } from '../../../lib/mock/viag/learn/data/mockData';

const CertificatesPage = () => {
  const { t, locale } = useLanguage();
  const [certificates, setCertificates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('valid'); // 'valid' or 'expired'

  useEffect(() => {
    // Simulate API call to get certificates
    const fetchCertificates = async () => {
      setTimeout(() => {
        setCertificates(MOCK_DATA.certificates);
        setLoading(false);
      }, 800);
    };

    fetchCertificates();
  }, []);

  // Filter certificates based on search term and active tab
  const filteredCertificates = certificates.filter(certificate => {
    const matchesSearch = 
      searchTerm === '' ||
      (locale === 'vi' ? 
        certificate.name.toLowerCase().includes(searchTerm.toLowerCase()) :
        certificate.nameEn.toLowerCase().includes(searchTerm.toLowerCase())) ||
      certificate.certificateCode.toLowerCase().includes(searchTerm.toLowerCase()) ||
      certificate.issuer.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesTab = 
      (activeTab === 'valid' && certificate.status === 'valid') ||
      (activeTab === 'expired' && certificate.status === 'expired');
    
    return matchesSearch && matchesTab;
  });

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-500"></div>
      </div>
    );
  }

  const validCertificatesCount = certificates.filter(cert => cert.status === 'valid').length;
  const expiredCertificatesCount = certificates.filter(cert => cert.status === 'expired').length;

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            {t('learn.certificates.title')}
          </h1>
          <p className="text-gray-500 mt-1">
            {t('learn.dashboard.certificatesEarned')}: {certificates.length}
          </p>
        </div>
        
        <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
          {/* Search box */}
          <div className="relative w-full sm:w-64">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="text"
              className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
              placeholder={t('learn.myCourses.searchPlaceholder')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>
      </div>

      {/* Certificate summary */}
      <div className="bg-indigo-50 border border-indigo-100 rounded-lg p-4">
        <h2 className="text-sm font-semibold text-indigo-800 mb-2">{t('learn.dashboard.certificates')}</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="flex items-center gap-4">
            <div className="bg-green-100 p-3 rounded-lg">
              <CheckCircleIcon className="h-5 w-5 text-green-600" />
            </div>
            <div>
              <div className="text-sm text-gray-500">{t('learn.certificates.validCertificates')}</div>
              <div className="text-2xl font-bold text-green-700">{validCertificatesCount}</div>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <div className="bg-red-100 p-3 rounded-lg">
              <ExclamationTriangleIcon className="h-5 w-5 text-red-600" />
            </div>
            <div>
              <div className="text-sm text-gray-500">{t('learn.certificates.expiredCertificates')}</div>
              <div className="text-2xl font-bold text-red-700">{expiredCertificatesCount}</div>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('valid')}
            className={`whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'valid'
                ? 'border-indigo-500 text-indigo-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            {t('learn.certificates.validCertificates')}
            <span className="ml-2 py-0.5 px-2 rounded-full text-xs bg-indigo-100 text-indigo-700">
              {validCertificatesCount}
            </span>
          </button>
          <button
            onClick={() => setActiveTab('expired')}
            className={`whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'expired'
                ? 'border-indigo-500 text-indigo-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            {t('learn.certificates.expiredCertificates')}
            <span className="ml-2 py-0.5 px-2 rounded-full text-xs bg-red-100 text-red-700">
              {expiredCertificatesCount}
            </span>
          </button>
        </nav>
      </div>

      {/* Certificates List */}
      {filteredCertificates.length > 0 ? (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {filteredCertificates.map(certificate => (
            <div key={certificate.id} className="bg-white rounded-lg shadow overflow-hidden border border-gray-200">
              <div className="p-6">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-4">
                    <div className={`p-3 rounded-lg ${
                      certificate.status === 'valid' ? 'bg-green-100' : 'bg-red-100'
                    }`}>
                      <AcademicCapIcon className={`h-6 w-6 ${
                        certificate.status === 'valid' ? 'text-green-600' : 'text-red-600'
                      }`} />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">
                        {locale === 'vi' ? certificate.name : certificate.nameEn}
                      </h3>
                      <p className="text-sm text-gray-500 mt-1">{certificate.certificateCode}</p>
                    </div>
                  </div>
                  <div className={`px-2 py-1 rounded-full text-xs font-medium ${
                    certificate.status === 'valid' 
                      ? 'bg-green-100 text-green-800' 
                      : 'bg-red-100 text-red-800'
                  }`}>
                    {certificate.status === 'valid' 
                      ? t('learn.certificates.validCertificates')
                      : t('learn.certificates.expiredCertificates')
                    }
                  </div>
                </div>
                
                <div className="mt-4 space-y-2">
                  <div className="flex justify-between items-center text-sm">
                    <span className="text-gray-500">{t('learn.certificates.issueDate')}:</span>
                    <span className="font-medium">{certificate.issueDate}</span>
                  </div>
                  <div className="flex justify-between items-center text-sm">
                    <span className="text-gray-500">{t('learn.certificates.expiryDate')}:</span>
                    <span className={`font-medium ${
                      certificate.status === 'expired' ? 'text-red-600' : ''
                    }`}>
                      {certificate.expiryDate}
                    </span>
                  </div>
                  <div className="flex justify-between items-center text-sm">
                    <span className="text-gray-500">{t('course-content.detail.createdBy')}:</span>
                    <span className="font-medium">{certificate.issuer}</span>
                  </div>
                  <div className="flex justify-between items-center text-sm">
                    <span className="text-gray-500">{t('play.exercise')}:</span>
                    <span className="font-medium">{certificate.linkedCourseName || 'N/A'}</span>
                  </div>
                </div>
                
                <div className="mt-6 grid grid-cols-2 gap-4">
                  <button
                    className="flex justify-center items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                  >
                    <ArrowDownTrayIcon className="h-4 w-4 mr-2" />
                    {t('learn.certificates.downloadCertificate')}
                  </button>
                  <button
                    className="flex justify-center items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                  >
                    <EyeIcon className="h-4 w-4 mr-2" />
                    {t('learn.certificates.viewDetails')}
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="bg-white p-6 rounded-lg shadow text-center">
          <DocumentTextIcon className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">
            {activeTab === 'valid' 
              ? t('learn.certificates.noValidCertificates')
              : t('learn.certificates.noExpiredCertificates')
            }
          </h3>
        </div>
      )}
    </div>
  );
};

export default CertificatesPage; 